import { useService } from '@toeverything/infra';
import { useEffect } from 'react';

import type { AutomationConfig } from '../services/automation';
import { WorkflowService } from '../services/workflow';

/**
 * Caches the selected workflow's step summaries on the automation block for
 * its local-first canvas projection. The request belongs to the open
 * automation sidebar (one owner per block), never to canvas rendering.
 * Persisting the source ID also makes a legitimately empty workflow
 * cacheable.
 */
export function useWorkflowStepCache(
  config: AutomationConfig | null,
  onUpdate: (updates: Partial<AutomationConfig>) => void
) {
  const workflowService = useService(WorkflowService);
  const workflowId = config?.workflowId;
  const cachedSourceId = config?.workflowStepsSourceId;

  useEffect(() => {
    if (!workflowId || cachedSourceId === workflowId) return;

    let cancelled = false;
    void workflowService
      .loadWorkflowStepSummaries(workflowId)
      .then(workflowSteps => {
        if (cancelled) return;
        onUpdate({ workflowSteps, workflowStepsSourceId: workflowId });
      })
      .catch(error => {
        console.error(
          '[AutomationPanel] Failed to cache workflow steps on automation block:',
          error
        );
      });

    return () => {
      cancelled = true;
    };
  }, [cachedSourceId, onUpdate, workflowId, workflowService]);
}
